import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ShieldCheck } from '../icons';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '../ui';
import { useImportHubReadOnly } from '../lib/access';
import {
  fetchImportRulesets,
  formatRulesetDay,
  RULESET_JOB_VERBS,
  manualChangeNote,
  rulesetStatusParts,
} from '../lib/ruleset-service';
import type { ImportRuleset } from '../lib/ruleset-service';
import { useRulesetJob } from '../lib/use-ruleset-job';
import RulesetChangesDialog from './RulesetChangesDialog';

type PendingAction = { action: 'apply' | 'deactivate'; ruleset: ImportRuleset };

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * The database Settings page's ruleset picker: apply or turn off one of this
 * database's saved rulesets. Rulesets are created and edited on the Documents
 * tab's Database Rules page.
 */
const DatabaseRulesSettings = ({
  databaseId,
  databaseName,
}: {
  databaseId: string;
  databaseName: string;
}): JSX.Element => {
  const readOnly = useImportHubReadOnly();
  const history = useHistory();
  const location = useLocation();
  const [rulesets, setRulesets] = React.useState<ImportRuleset[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState('');
  const [pending, setPending] = React.useState<PendingAction | null>(null);
  const [changesRuleset, setChangesRuleset] =
    React.useState<ImportRuleset | null>(null);

  const loadRulesets = React.useCallback(async () => {
    const loaded = await fetchImportRulesets(databaseId);
    setLoading(false);
    if (!loaded.success) {
      setError(loaded.error ?? 'Failed to load rulesets');
      return;
    }
    setError(null);
    setRulesets(loaded.rulesets);
    setSelectedId((current) =>
      loaded.rulesets.some((r) => r.id === current)
        ? current
        : (loaded.rulesets.find((r) => r.is_active) ?? loaded.rulesets[0])
            ?.id ?? '',
    );
  }, [databaseId]);

  React.useEffect(() => {
    setLoading(true);
    void loadRulesets();
  }, [loadRulesets]);

  const { runningJob, run } = useRulesetJob(
    databaseId,
    databaseName,
    loadRulesets,
  );
  const busy = runningJob !== null;
  const selected = rulesets.find((r) => r.id === selectedId) ?? null;
  const activeRuleset = rulesets.find((r) => r.is_active) ?? null;

  const openDatabaseRules = () =>
    history.push({
      pathname: location.pathname,
      search: '?tab=documents&tool=rules',
    });

  const confirm = () => {
    if (!pending) return;
    const { action, ruleset } = pending;
    setPending(null);
    void run(ruleset.id, action, ruleset.name);
  };

  const renderBody = () => {
    if (loading) {
      return <p className='ih-db-settings-help'>Loading rulesets…</p>;
    }
    if (error) {
      return (
        <div className='ih-db-rules-empty'>
          <span>{error}</span>
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              setLoading(true);
              void loadRulesets();
            }}
          >
            Retry
          </Button>
        </div>
      );
    }
    if (rulesets.length === 0) {
      return (
        <div className='ih-db-rules-empty'>
          <span>
            No saved rulesets yet. Upload an SOP in Database Rules to create
            one.
          </span>
          <Button variant='outline' size='sm' onClick={openDatabaseRules}>
            Go to Database Rules
          </Button>
        </div>
      );
    }
    return (
      <>
        <div className='ih-db-settings-grid'>
          <label>
            <span>Ruleset</span>
            <Select
              value={selectedId}
              onValueChange={setSelectedId}
              disabled={busy}
            >
              {rulesets.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.is_active ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </label>
        </div>
        {selected && (
          <div className='ih-db-rules-status'>
            <div className='ih-db-rules-status-text'>
              <strong>
                {selected.name}
                {selected.is_active && (
                  <span className='ih-db-rules-badge'>Active</span>
                )}
              </strong>
              <small>
                {plural(selected.rules.length, 'rule')} ·{' '}
                {selected.source_filename ?? 'SOP'}
              </small>
              <small>
                {selected.is_active
                  ? [
                      `Applied ${formatRulesetDay(selected.applied_at)}`,
                      ...rulesetStatusParts(selected),
                    ].join(' · ')
                  : 'Not active on this database'}
              </small>
            </div>
            <div className='ih-db-rules-actions'>
              {selected.is_active ? (
                <>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setChangesRuleset(selected)}
                  >
                    View changes
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    locked={readOnly}
                    disabled={busy}
                    onClick={() =>
                      setPending({ action: 'apply', ruleset: selected })
                    }
                  >
                    Re-apply
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    locked={readOnly}
                    disabled={busy}
                    onClick={() =>
                      setPending({ action: 'deactivate', ruleset: selected })
                    }
                  >
                    Turn off
                  </Button>
                </>
              ) : (
                <Button
                  variant='primary'
                  size='sm'
                  locked={readOnly}
                  disabled={busy || selected.rules.length === 0}
                  onClick={() =>
                    setPending({ action: 'apply', ruleset: selected })
                  }
                >
                  Apply
                </Button>
              )}
            </div>
          </div>
        )}
        {runningJob && (
          <div className='ih-db-rules-job' role='status'>
            <span className='ih-spinner ih-db-settings-spinner' />
            <span>
              {RULESET_JOB_VERBS[runningJob.action]} “{runningJob.name}”…
              <span className='ih-db-rules-job-progress'>
                {runningJob.total > 0
                  ? ` ${runningJob.processed.toLocaleString()} of ${runningJob.total.toLocaleString()} products`
                  : ' Starting'}
              </span>
            </span>
          </div>
        )}
        <div>
          <Button variant='link' size='sm' onClick={openDatabaseRules}>
            Manage rulesets in Database Rules
          </Button>
        </div>
      </>
    );
  };

  return (
    <section className='ih-db-settings-section'>
      <div className='ih-db-settings-section-title'>
        <ShieldCheck size={15} />
        Database Rules
      </div>
      <p className='ih-db-settings-help'>
        Apply a saved ruleset to turn FTAs and exclusions on or off for this
        database&apos;s products. One ruleset is active at a time.
      </p>
      {renderBody()}

      <RulesetChangesDialog
        databaseId={databaseId}
        ruleset={changesRuleset}
        onClose={() => setChangesRuleset(null)}
      />

      <Dialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        {pending && (
          <DialogContent size='md' onClose={() => setPending(null)}>
            <DialogHeader>
              <DialogTitle>
                {pending.action === 'deactivate'
                  ? 'Turn off'
                  : pending.ruleset.is_active
                  ? 'Re-apply'
                  : 'Apply'}{' '}
                “{pending.ruleset.name}”?
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              {pending.action === 'deactivate' ? (
                <p className='ih-text-sm ih-text-muted'>
                  Products in <strong>{databaseName}</strong> go back to the
                  FTAs and exclusions they had before this ruleset, except codes
                  someone has changed since.
                </p>
              ) : (
                <>
                  <p className='ih-text-sm ih-text-muted'>
                    {plural(pending.ruleset.rules.length, 'rule')} will be
                    applied to <strong>{databaseName}</strong>. Products will be
                    checked against these rules and updated where they don’t
                    comply.
                  </p>
                  <p className='ih-text-sm ih-text-muted ih-mt-2'>
                    {manualChangeNote(
                      pending.ruleset.is_active
                        ? pending.ruleset.manual_changes ?? 0
                        : 0,
                    )}
                  </p>
                  {activeRuleset && activeRuleset.id !== pending.ruleset.id && (
                    <p className='ih-text-sm ih-text-muted ih-mt-2'>
                      This replaces the active ruleset,{' '}
                      <strong>{activeRuleset.name}</strong>; the changes it made
                      are undone first.
                    </p>
                  )}
                </>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button variant='primary' locked={readOnly} onClick={confirm}>
                {pending.action === 'deactivate'
                  ? 'Turn off'
                  : pending.ruleset.is_active
                  ? 'Re-apply'
                  : 'Apply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
};

export default DatabaseRulesSettings;
